import { RubyGemMark } from '../../components/RubyGemMark';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp, Plus, Save, Trash2, Settings2, Copy, GripVertical, X, ImageOff, FileText, ArrowLeft } from 'lucide-react';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import SearchableDropdown from '../../components/SearchableDropdown';
import ShimmerImage from '../../components/ShimmerImage';
import ProvisioningDevicePicker from './ProvisioningDevicePicker';
import { stageById, stageOrder, stagesForModel } from './provisioningStages';
import { toastService } from '../../services/toastService';
import {
  hardwareApi, CATEGORY_LABEL, CATEGORY_CHIP, formFactorLabel,
  type DeviceModelRow, type ProvisioningCategory, type ProvisioningStepInput,
} from '../../services/hardwareApi';
import { RubyLoader } from '../../components/RubyLoader';

/**
 * PROVISIONING TEMPLATES — mockup screen E, the Provisioning tab of the
 * Inventory hub. Per-model checklist editor: categories (add/rename/remove/
 * reorder), steps (add/edit/remove/reorder within a category, `required`
 * toggle). Plain ↑↓ buttons for reorder — no drag library, per the F1 brief.
 *
 * `PUT /lifecycle/templates/{modelId}` is a WHOLESALE replace, so Save always
 * sends the full categories+steps list — this editor keeps its own local
 * copy and only calls the API on Save, guarded by a dirty flag so a model
 * switch (or a tab switch) with unsaved edits is never silently discarded.
 */

const genId = (prefix: string) => `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;

/**
 * One draggable row. The handle is a DEDICATED grip, not the whole row: the
 * row contains text inputs, and making it all draggable would swallow every
 * click into the field and make the checklist un-editable.
 *
 * The ↑↓ buttons are deliberately KEPT alongside the grip — dnd-kit's pointer
 * sensor is awkward on touch, and this admin also ships inside the iOS webview.
 */
const Sortable: React.FC<{
  id: string;
  children: (handle: React.ReactNode, dragging: boolean) => React.ReactNode;
}> = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  // `setNodeRef` marks the DRAGGABLE NODE (the whole row); the listeners go on
  // the grip alone. Putting the ref on the button would make dnd-kit measure
  // and animate the handle instead of the row.
  const handle = (
    <button
      {...attributes}
      {...listeners}
      className="cursor-grab active:cursor-grabbing p-1 text-stone-300 hover:text-stone-600 touch-none"
      title="Drag to reorder"
      aria-label="Drag to reorder"
    >
      <GripVertical size={14} />
    </button>
  );
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.55 : 1 }}
      className={isDragging ? 'relative z-10' : undefined}
    >
      {children(handle, isDragging)}
    </div>
  );
};

const reindex = <T extends { order?: number }>(items: T[]): T[] => items.map((it, i) => ({ ...it, order: i }));

/** Hero shot, falling back to the first gallery photo so a model that was only
 *  ever given photos still shows a picture in the dropdown. */
const thumbOf = (m: DeviceModelRow) => m.media?.heroImageUrl || m.media?.photos?.[0];

const Fact: React.FC<{ label: string; children: React.ReactNode; mono?: boolean }> = ({ label, children, mono }) => (
  <div className="min-w-0">
    <div className="text-[9.5px] font-semibold uppercase tracking-wide text-stone-400">{label}</div>
    <div className={`truncate text-[11px] text-stone-800 ${mono ? 'font-mono' : ''}`}>{children}</div>
  </div>
);

/**
 * THE DEVICE, IN FRONT OF YOU while you write its checklist.
 *
 * A step like "seat the cable in the lower port" is only writable — and only
 * checkable by whoever works the line — if the person writing it can see the
 * machine. Pictures come from the device record itself (hero + gallery), so
 * nothing new has to be uploaded here; the gallery is clickable because the
 * port or switch a step talks about is usually in the second photo, not the
 * hero shot.
 */
const DeviceCard: React.FC<{ model: DeviceModelRow }> = ({ model }) => {
  const photos = useMemo(() => {
    const all = [model.media?.heroImageUrl, ...(model.media?.photos || [])].filter(Boolean) as string[];
    return Array.from(new Set(all));
  }, [model]);
  const [active, setActive] = useState(0);
  // A model switch must reset the gallery, or photo #3 of the last device
  // stays selected and the card shows the wrong picture.
  useEffect(() => { setActive(0); }, [model.modelId]);

  // Primitives only — a nested spec object renders as "[object Object]", which
  // is worse than not showing the row at all.
  const specEntries = Object.entries(model.specs || {})
    .filter(([, v]) => v !== null && v !== undefined && v !== '' && typeof v !== 'object')
    .slice(0, 6);

  return (
    <div className="rounded-xl border border-stone-200 bg-white overflow-hidden">
      <div className="bg-stone-50 border-b border-stone-200 p-3">
        <ShimmerImage
          src={photos[active]}
          alt={model.modelName}
          width="100%"
          height={170}
          objectFit="contain"
          className="rounded-lg bg-white border border-stone-200"
          fallbackBgColor="bg-white"
          fallbackIcon={<ImageOff size={26} className="text-stone-300" />}
        />
        {photos.length > 1 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {photos.map((p, i) => (
              <button
                key={p}
                onClick={() => setActive(i)}
                title={`Photo ${i + 1}`}
                className={`rounded-md border p-0.5 ${i === active ? 'border-red-400' : 'border-stone-200 hover:border-stone-400'}`}
              >
                <ShimmerImage src={p} alt={`${model.modelName} photo ${i + 1}`} width={40} height={40} objectFit="contain" className="rounded" />
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="p-3.5">
        <div className="text-sm font-bold text-stone-900">{model.modelName}</div>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${CATEGORY_CHIP[model.category] || 'bg-stone-100 text-stone-600'}`}>
            {CATEGORY_LABEL[model.category] || model.category}
          </span>
          {model.brand && <span className="rounded bg-stone-200 px-1.5 py-0.5 text-[10px] font-semibold text-stone-700">{model.brand}</span>}
          <span className="text-[11px] text-stone-500">{model.manufacturer}</span>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2.5">
          <Fact label="Device ID" mono>{model.modelId}</Fact>
          <Fact label="Form factor">{formFactorLabel(model.appProfile?.terminalFormFactor)}</Fact>
          <Fact label="Tracking">{model.trackingMode === 'quantity' ? 'Total inventory' : 'Individual units'}</Fact>
          <Fact label="Sub-category">{model.subCategory || '—'}</Fact>
        </div>

        {model.description && (
          <p className="mt-3 text-[11px] leading-relaxed text-stone-600">{model.description}</p>
        )}

        {specEntries.length > 0 && (
          <div className="mt-3 border-t border-stone-100 pt-2.5">
            <div className="mb-1.5 text-[9.5px] font-semibold uppercase tracking-wide text-stone-400">Specs</div>
            <dl className="space-y-1">
              {specEntries.map(([k, v]) => (
                <div key={k} className="flex items-start justify-between gap-3 text-[11px]">
                  <dt className="text-stone-500 capitalize">{k.replace(/[_-]/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2')}</dt>
                  <dd className="min-w-0 truncate text-right font-mono text-stone-700">{String(v)}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}

        {(model.media?.datasheetUrl || (model.documents || []).length > 0) && (
          <div className="mt-3 flex flex-wrap gap-1.5 border-t border-stone-100 pt-2.5">
            {model.media?.datasheetUrl && (
              <a
                href={model.media.datasheetUrl} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 rounded-lg border border-stone-300 bg-white px-2 py-1 text-[10.5px] font-semibold text-stone-700 hover:bg-stone-50"
              >
                <FileText size={11} /> Datasheet
              </a>
            )}
            {(model.documents || []).slice(0, 4).map((d) => (
              <a
                key={d.url} href={d.url} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 rounded-lg border border-stone-300 bg-white px-2 py-1 text-[10.5px] font-semibold text-stone-700 hover:bg-stone-50"
              >
                <FileText size={11} /> {d.name}
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const ProvisioningTemplateEditor: React.FC = () => {
  const [models, setModels] = useState<DeviceModelRow[]>([]);
  const [modelId, setModelId] = useState<string>('');
  const [steps, setSteps] = useState<ProvisioningStepInput[]>([]);
  const [usedByCount, setUsedByCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modelsLoading, setModelsLoading] = useState(true);
  /** Leaving the editor while `dirty` — either to another model or back to the
   * device list — is shown as an inline confirmation rather than applied
   * immediately; never a native `confirm()`, which is a silent no-op inside
   * the iOS webview this app also ships in. */
  const [pendingLeave, setPendingLeave] = useState<{ to: string } | null>(null);

  /** Copy-from-another-model panel. */
  const [copyOpen, setCopyOpen] = useState(false);
  const [copySourceId, setCopySourceId] = useState('');
  const [copyMode, setCopyMode] = useState<'replace' | 'merge'>('replace');
  const [copying, setCopying] = useState(false);
  /** Which step's instructions are expanded. Only one at a time — the panel is tall. */
  const [openNote, setOpenNote] = useState<string | null>(null);

  const sensors = useSensors(
    // A small distance threshold so a click into a text field is never
    // mistaken for the start of a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    // No model is auto-selected: step 1 is the device gallery, and landing
    // straight inside some arbitrary first device's checklist is exactly the
    // confusion the two-step flow removes.
    hardwareApi.listModels()
      .then((r) => setModels(r.items || []))
      .catch((e) => setError(e?.message || 'Failed to load device models'))
      .finally(() => setModelsLoading(false));
  }, []);

  const loadTemplate = useCallback(async (mid: string) => {
    if (!mid) return;
    setLoading(true);
    setError(null);
    try {
      const res = await hardwareApi.getProvisioningTemplate(mid);
      /*
        ONLY STEPS FILED UNDER A REAL STAGE ARE LOADED.

        Groups are no longer authored, so a step pointing at some old free-text
        category names a group that does not exist any more. Those are dropped
        on load rather than drawn in a limbo section — the alternative is a
        screen that shows work nowhere on the order will ever ask for.
      */
      const stps = [...(res.template.steps || [])]
        .filter((s) => !!stageById(s.categoryId))
        .sort((a, b) => stageOrder(a.categoryId) - stageOrder(b.categoryId) || (a.order ?? 100) - (b.order ?? 100));
      setSteps(stps);
      setDirty(false);
      // Usage count is informational only — a model with units already
      // provisioning still gets to edit its template (a unit already in
      // flight keeps its OWN snapshot; this only affects the next one).
      try {
        const usage = await hardwareApi.listUnits({ modelId: mid, limit: 1 });
        setUsedByCount(usage.count ?? null);
      } catch { setUsedByCount(null); }
    } catch (e: any) {
      setError(e?.message || 'Failed to load this template');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (modelId) loadTemplate(modelId); }, [modelId, loadTemplate]);

  const modelOptions = useMemo(
    () => models.map((m) => ({
      value: m.modelId,
      label: m.modelName,
      subtitle: [m.brand || m.manufacturer, CATEGORY_LABEL[m.category] || m.category].filter(Boolean).join(' · '),
      imageUrl: thumbOf(m),
    })),
    [models],
  );

  const selectedModel = models.find((m) => m.modelId === modelId);

  /**
   * THE SECTIONS OF THIS TEMPLATE — fixed, in journey order, and decided by
   * the device's own lane. Nobody adds, renames, reorders or deletes one.
   */
  const stages = useMemo(() => stagesForModel(selectedModel), [selectedModel]);

  const stepsByCategory = useCallback(
    (categoryId: string) => steps.filter((s) => s.categoryId === categoryId).sort((a, b) => (a.order ?? 100) - (b.order ?? 100)),
    [steps],
  );

  // -- step ops -----------------------------------------------------------
  const addStep = (categoryId: string) => {
    const stepId = genId('step');
    setSteps((prev) => {
      const inCat = prev.filter((s) => s.categoryId === categoryId);
      return [...prev, { stepId, label: 'New step', categoryId, required: true, order: inCat.length }];
    });
    setDirty(true);
  };
  const editStep = (stepId: string, patch: Partial<ProvisioningStepInput>) => {
    setSteps((prev) => prev.map((s) => (s.stepId === stepId ? { ...s, ...patch } : s)));
    setDirty(true);
  };
  const removeStep = (stepId: string) => {
    setSteps((prev) => prev.filter((s) => s.stepId !== stepId));
    setDirty(true);
  };
  const moveStep = (stepId: string, categoryId: string, dir: -1 | 1) => {
    setSteps((prev) => {
      const inCat = prev.filter((s) => s.categoryId === categoryId).sort((a, b) => (a.order ?? 100) - (b.order ?? 100));
      const idx = inCat.findIndex((s) => s.stepId === stepId);
      const swap = idx + dir;
      if (idx < 0 || swap < 0 || swap >= inCat.length) return prev;
      [inCat[idx], inCat[swap]] = [inCat[swap], inCat[idx]];
      const reordered = reindex(inCat);
      const others = prev.filter((s) => s.categoryId !== categoryId);
      return [...others, ...reordered];
    });
    setDirty(true);
  };

  // -- copy from another model ------------------------------------------------
  /**
   * Load another model's checklist into THIS editor as an unsaved draft.
   *
   * Step ids are REGENERATED, never reused: a copied step is a new step, and
   * sharing an id with the original would make the two indistinguishable in
   * any audit — and on a merge, collide with what is already here.
   *
   * STAGES ARE NOT REMAPPED, because they are not the source's to give: both
   * templates file their steps under the same fixed stage ids. What CAN differ
   * is which stages each device's lane has — copying a till's coupling steps
   * onto a cash drawer would file work under a stage that device never
   * reaches, so those steps are dropped and the count says how many.
   *
   * Nothing is written until Save — this is "copy it, then change it", so the
   * operator reviews the result against the model it is actually for.
   */
  const applyCopy = async () => {
    if (!copySourceId || copySourceId === modelId) return;
    setCopying(true);
    setError(null);
    try {
      const res = await hardwareApi.getProvisioningTemplate(copySourceId);
      const srcSteps = [...(res.template.steps || [])].sort(
        (a, b) => stageOrder(a.categoryId) - stageOrder(b.categoryId) || (a.order ?? 100) - (b.order ?? 100),
      );

      if (srcSteps.length === 0) {
        setError('That model has no checklist to copy.');
        return;
      }

      const here = new Set(stages.map((s) => s.id));
      const usable = srcSteps.filter((st) => here.has(st.categoryId));
      const dropped = srcSteps.length - usable.length;

      if (usable.length === 0) {
        setError(`None of those ${srcSteps.length} step(s) belong to a stage this device has.`);
        return;
      }

      const newSteps = usable.map((st) => ({
        stepId: genId('step'),
        label: st.label,
        categoryId: st.categoryId,
        required: st.required ?? true,
        order: st.order ?? 100,
        ...(st.instructions ? { instructions: st.instructions } : {}),
      }));

      if (copyMode === 'replace') {
        setSteps(newSteps);
      } else {
        // Merge: appended after what is already here, so existing steps keep
        // their position and nothing is silently reordered under the operator.
        setSteps((prev) => [...prev, ...newSteps]);
      }
      setDirty(true);
      setCopyOpen(false);
      const srcName = models.find((m) => m.modelId === copySourceId)?.modelName || copySourceId;
      toastService.success(
        `Copied ${newSteps.length} step(s) from ${srcName}${dropped > 0 ? ` — ${dropped} skipped, on a stage this device has not got` : ''}. Review and Save — nothing is stored yet.`,
      );
    } catch (e: any) {
      setError(e?.message || 'Could not copy that template');
    } finally {
      setCopying(false);
    }
  };

  // -- drag and drop ----------------------------------------------------------
  /**
   * STEPS ONLY. The stages themselves are fixed in journey order and cannot be
   * dragged — reordering them would claim packing happens before testing.
   *
   * A step may be dragged ONTO another stage, which moves it there: the step
   * carries its own `categoryId`, so that is a genuine reparent, not just a
   * reorder, and it is how a step written in the wrong place gets rehomed.
   */
  const onDragEnd = (e: DragEndEvent) => {
    const activeId = String(e.active.id);
    const overId = e.over ? String(e.over.id) : '';
    if (!overId || activeId === overId) return;

    const activeStep = steps.find((st) => st.stepId === activeId);
    if (!activeStep) return;

    // Dropped on a stage header = move into that stage, at the end.
    const overCategory = stages.find((c) => c.id === overId);
    const overStep = steps.find((st) => st.stepId === overId);
    const targetCatId = overCategory ? overCategory.id : overStep?.categoryId;
    if (!targetCatId) return;

    setSteps((prev) => {
      const others = prev.filter((st) => st.categoryId !== targetCatId && st.stepId !== activeId);
      const target = prev
        .filter((st) => st.categoryId === targetCatId && st.stepId !== activeId)
        .sort((a, b) => (a.order ?? 100) - (b.order ?? 100));
      const moved = { ...activeStep, categoryId: targetCatId };
      const at = overStep ? target.findIndex((st) => st.stepId === overStep.stepId) : target.length;
      target.splice(at < 0 ? target.length : at, 0, moved);
      return [...others, ...reindex(target)];
    });
    setDirty(true);
  };

  const save = async () => {
    if (!modelId) return;
    setSaving(true);
    setError(null);
    try {
      /*
        THE STAGES ARE SENT AS THE TEMPLATE'S CATEGORIES.

        That is the whole trick that kept this off the backend: the service
        already stores a category list and validates that every step names one
        of them, so writing the fixed stages into it — id = the journey's
        stage id, label = what the order screen calls that stage — needed no
        new field and no new rule. The label is what gets snapshotted onto a
        unit, which is how a stage panel later recognises its own steps.

        EVERY stage this device has is sent, not just the ones carrying steps:
        a step dragged into an empty stage must have a category to point at,
        and an empty one costs a row nobody sees.
      */
      const categories: ProvisioningCategory[] = stages.map((s, i) => ({ id: s.id, label: s.label, order: i }));
      const res = await hardwareApi.putProvisioningTemplate(modelId, { categories, steps });
      const stps = [...(res.template.steps || [])]
        .filter((s) => !!stageById(s.categoryId))
        .sort((a, b) => stageOrder(a.categoryId) - stageOrder(b.categoryId) || (a.order ?? 100) - (b.order ?? 100));
      setSteps(stps);
      setDirty(false);
      const used = new Set(steps.map((s) => s.categoryId)).size;
      toastService.success(`Saved — ${steps.length} step(s) across ${used} stage(s).`);
    } catch (e: any) {
      setError(e?.message || 'Failed to save this template');
    } finally {
      setSaving(false);
    }
  };

  /** `mid` of `''` (or null, from the dropdown's clear) means "back to the device list". */
  const switchModel = (mid: string | null) => {
    const to = mid || '';
    if (to === modelId) return;
    if (dirty) { setPendingLeave({ to }); return; }
    setModelId(to);
  };

  // -- STEP 1: pick the device ------------------------------------------------
  if (!modelId) {
    return (
      <div>
        {error && (
          <div className="mb-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3">
            <AlertTriangle size={16} className="mt-0.5 shrink-0 text-red-600" />
            <div className="text-sm text-red-800">{error}</div>
          </div>
        )}
        <ProvisioningDevicePicker models={models} loading={modelsLoading} onPick={setModelId} />
      </div>
    );
  }

  // -- STEP 2: that device's checklist ---------------------------------------
  return (
    <div>
      <button
        onClick={() => switchModel('')}
        className="mb-3 inline-flex min-h-[36px] items-center gap-1.5 rounded-lg border border-stone-300 bg-white px-3 text-[11px] font-semibold text-stone-600 hover:text-stone-900"
      >
        <ArrowLeft size={13} /> All devices
      </button>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div className="max-w-md flex-1 min-w-[16rem]">
          <label className="block text-[10px] uppercase tracking-wider text-stone-500 mb-1 font-mono">Model template</label>
          <SearchableDropdown
            options={modelOptions}
            value={modelId || null}
            onChange={switchModel}
            placeholder="-- Select a device model --"
            variant="light"
          />
          {selectedModel && (
            <p className="mt-1.5 text-[11px] text-stone-500">
              {steps.length} step(s) across {stages.length} stage(s)
              {typeof usedByCount === 'number' && ` · used by ${usedByCount} unit(s)`}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCopyOpen((v) => !v)}
            disabled={!modelId || models.length < 2}
            title={models.length < 2 ? 'Needs another model to copy from' : 'Copy a checklist from another device'}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-stone-300 bg-white text-stone-700 text-sm font-semibold disabled:opacity-40 min-h-[44px]"
          >
            <Copy size={14} /> Copy from…
          </button>
          <button
            onClick={save}
            disabled={!modelId || !dirty || saving}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-40 min-h-[44px]"
            style={{ background: '#DC143C' }}
          >
            {saving ? <RubyGemMark size={14} drawing color="currentColor" /> : <Save size={14} />}
            {dirty ? 'Save changes' : 'Saved'}
          </button>
        </div>
      </div>

      {copyOpen && (
        <div className="mb-4 rounded-xl border border-stone-300 bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-stone-800">Copy a checklist from another device</h3>
            <button onClick={() => setCopyOpen(false)} className="p-1 text-stone-400 hover:text-stone-700"><X size={15} /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr_auto] gap-3 items-end">
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-stone-500 mb-1 font-mono">Source model</label>
              <SearchableDropdown
                options={models
                  .filter((m) => m.modelId !== modelId)
                  .map((m) => ({
                    value: m.modelId,
                    label: m.modelName,
                    subtitle: [m.brand || m.manufacturer, CATEGORY_LABEL[m.category] || m.category].filter(Boolean).join(' · '),
                    imageUrl: thumbOf(m),
                  }))}
                value={copySourceId || null}
                onChange={(v) => setCopySourceId(v || '')}
                placeholder="-- Copy from --"
                searchPlaceholder="Search models…"
                variant="light"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-stone-500 mb-1 font-mono">Mode</label>
              <SearchableDropdown
                options={[
                  { value: 'replace', label: 'Replace', subtitle: 'Discard this model’s current checklist' },
                  { value: 'merge', label: 'Add to existing', subtitle: 'Append after what is already here' },
                ]}
                value={copyMode}
                onChange={(v) => v && setCopyMode(v as 'replace' | 'merge')}
                placeholder="Mode"
                variant="light"
              />
            </div>
            <button
              onClick={applyCopy}
              disabled={!copySourceId || copying}
              className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-40 min-h-[44px]"
              style={{ background: '#DC143C' }}
            >
              {copying ? <RubyGemMark size={14} drawing color="currentColor" /> : <Copy size={14} />} Copy
            </button>
          </div>
          <p className="mt-2 text-[11px] text-stone-500">
            Steps are copied as NEW steps with their own ids and their instructions, then loaded here
            unsaved — review them against this model and press Save. Nothing is written until you do,
            and the source model is not touched.
          </p>
        </div>
      )}

      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3">
          <AlertTriangle size={16} className="mt-0.5 shrink-0 text-red-600" />
          <div className="text-sm text-red-800">{error}</div>
        </div>
      )}

      {pendingLeave ? (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-[11px] text-amber-900">
          <span>
            <strong>Discard unsaved template changes</strong> and {pendingLeave.to ? 'switch models' : 'go back to the device list'}?
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPendingLeave(null)}
              className="min-h-[36px] rounded-lg border border-amber-300 bg-white px-3 text-[11px] font-semibold text-amber-900"
            >
              Keep editing
            </button>
            <button
              onClick={() => { const to = pendingLeave.to; setPendingLeave(null); setModelId(to); }}
              className="min-h-[36px] rounded-lg bg-amber-600 px-3 text-[11px] font-semibold text-white"
            >
              Discard and {pendingLeave.to ? 'switch' : 'leave'}
            </button>
          </div>
        </div>
      ) : dirty && (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] text-amber-900">
          Unsaved changes — switching models or leaving this tab without saving will discard them.
        </div>
      )}

      {loading ? (
        <RubyLoader variant="page" />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_.7fr] gap-5 items-start">
          <div className="space-y-4">
            {/*
              THE ORDER'S OWN LINE, drawn top to bottom in the order the work
              happens. Every section is a stage of the order journey and each
              one draws only its own steps — which is what makes a stage panel
              on the order screen able to show this device's work for THAT
              moment, instead of the whole plan in one place.
            */}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            {stages.map((cat, ci) => (
              <div key={cat.id} className="rounded-xl border border-stone-200 bg-white overflow-hidden mb-4">
                <div className="flex items-start gap-2 px-4 py-3 bg-stone-50 border-b border-stone-200">
                  <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-stone-200 font-mono text-[10px] font-bold text-stone-600">
                    {ci + 1}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-bold text-stone-800">{cat.label}</span>
                    <span className="mt-0.5 block text-[11px] leading-relaxed text-stone-500">{cat.hint}</span>
                  </span>
                  <span className="shrink-0 font-mono text-[10px] text-stone-400">{stepsByCategory(cat.id).length} step(s)</span>
                </div>
                <div className="p-3 space-y-1.5">
                  <SortableContext items={stepsByCategory(cat.id).map((st) => st.stepId)} strategy={verticalListSortingStrategy}>
                  {stepsByCategory(cat.id).map((s, si, arr) => (
                    <Sortable key={s.stepId} id={s.stepId}>{(stepHandle) => (
                    <div className="rounded-lg border border-stone-200 bg-white">
                      <div className="flex items-center gap-2 px-3 py-2">
                        {stepHandle}
                        <button
                          onClick={() => editStep(s.stepId, { required: !(s.required ?? true) })}
                          title={s.required ?? true ? 'Required — click to make optional' : 'Optional — click to make required'}
                          className={`w-5 h-5 rounded shrink-0 flex items-center justify-center text-[10px] font-bold border ${
                            (s.required ?? true) ? 'bg-red-100 border-red-300 text-red-700' : 'bg-stone-100 border-stone-300 text-stone-400'
                          }`}
                        >
                          {(s.required ?? true) ? '!' : '·'}
                        </button>
                        <input
                          value={s.label}
                          onChange={(e) => editStep(s.stepId, { label: e.target.value })}
                          className="flex-1 bg-transparent text-xs text-stone-700 focus:outline-none"
                        />
                        {/* Instructions: the whole point is that someone who has
                            never done this step can follow it, so the presence of
                            a note is visible without expanding. */}
                        <button
                          onClick={() => setOpenNote(openNote === s.stepId ? null : s.stepId)}
                          title={s.instructions ? 'Edit instructions' : 'Add instructions for whoever works this step'}
                          className={`px-1.5 py-0.5 rounded text-[10px] font-bold border ${
                            s.instructions
                              ? 'bg-blue-50 border-blue-200 text-blue-600'
                              : 'bg-white border-stone-200 text-stone-400 hover:text-stone-600'
                          }`}
                        >
                          {s.instructions ? 'How-to' : '+ How-to'}
                        </button>
                        <button onClick={() => moveStep(s.stepId, cat.id, -1)} disabled={si === 0} className="p-1 rounded text-stone-400 hover:text-stone-700 disabled:opacity-30"><ChevronUp size={13} /></button>
                        <button onClick={() => moveStep(s.stepId, cat.id, 1)} disabled={si === arr.length - 1} className="p-1 rounded text-stone-400 hover:text-stone-700 disabled:opacity-30"><ChevronDown size={13} /></button>
                        <button onClick={() => removeStep(s.stepId)} className="p-1 rounded text-red-400 hover:text-red-600"><Trash2 size={13} /></button>
                      </div>
                      {openNote === s.stepId && (
                        <div className="border-t border-stone-100 px-3 py-2.5">
                          <label className="block text-[10px] uppercase tracking-wider text-stone-500 mb-1 font-mono">
                            Instructions for this step
                          </label>
                          <textarea
                            value={s.instructions || ''}
                            onChange={(e) => editStep(s.stepId, { instructions: e.target.value || undefined })}
                            rows={3}
                            placeholder="What exactly to do, and how to tell it worked. Written for someone doing it for the first time."
                            className="w-full rounded-lg border border-stone-300 px-2.5 py-2 text-xs text-stone-700"
                          />
                          <p className="mt-1 text-[10px] text-stone-400">
                            Copied onto each unit when its provisioning starts, so the person ticking the
                            box reads the procedure that applied to that machine — later edits never
                            rewrite one already in flight.
                          </p>
                        </div>
                      )}
                    </div>
                    )}</Sortable>
                  ))}
                  </SortableContext>
                  <button
                    onClick={() => addStep(cat.id)}
                    className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-dashed border-stone-300 text-[11px] font-semibold text-stone-500 hover:text-stone-700 hover:border-stone-400"
                  >
                    <Plus size={12} /> Add step
                  </button>
                </div>
              </div>
            ))}
            </DndContext>
          </div>

          <div className="space-y-4">
            {selectedModel && (
              <div>
                <h3 className="text-xs font-bold text-stone-800 mb-2">The device</h3>
                <DeviceCard model={selectedModel} />
              </div>
            )}

            <h3 className="text-xs font-bold text-stone-800 mb-2">How templates behave</h3>
            <div className="text-[11px] text-stone-500 leading-relaxed space-y-2.5 rounded-xl border border-stone-200 bg-white p-4">
              <p><strong className="text-stone-700">The stages are the order's stages</strong> — they are fixed in code and nobody adds or reorders one. A step written under a stage is shown at exactly that point of the order, for this device only, and nowhere else.</p>
              <p><strong className="text-stone-700">This device's lane decides the list</strong> — a card reader gets the payment lane's configure stage, a till gets its own and the OS install with it, and neither a drawer nor a printer is offered a coupling stage it will never reach.</p>
              <p><strong className="text-stone-700">Copy from another device</strong> — "Copy from…" loads another model's steps here (with their instructions), to replace or add to this one. Steps on a stage this device has not got are skipped. Nothing is written until you Save, and the source is never touched.</p>
              <p><strong className="text-stone-700">Drag to move a step</strong> — grab the handle to reorder a step, or drop it on another stage's header to rehome it. The ↑↓ buttons still work for touch.</p>
              <p><strong className="text-stone-700">Write the how-to</strong> — a step label reminds someone who already knows the job; the <span className="font-mono">How-to</span> note is for someone who does not. It travels onto the unit, so it is read where the work happens.</p>
              <p><strong className="text-stone-700">Editing is safe</strong> — a unit snapshots the template the moment provisioning starts, so a change here never rewrites a machine already in flight.</p>
              <p><strong className="text-stone-700">Steps can gate</strong> — the red <span className="font-mono">!</span> marks a step required before packing (default); click it to make a step informational only.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProvisioningTemplateEditor;
